/* eslint-disable no-nested-ternary */
import React, { useContext, useCallback, useMemo, useState, useEffect } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { space, flexbox, border, color, layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { useSelector, useDispatch } from 'react-redux';
import {
  getBillingSummaryRequest,
  getBillingDocumentsRequest,
  getBillingProfilesRequest,
  isWithrawAvailableRequest,
  getLastWithdrawalRequest,
} from 'redux-api/action/billing';
import selectSummary, {
  selectDocuments,
  selectDefaultBillingProfile,
  selectLastWithdrawal,
} from 'redux-api/reselect/billing';
import ContentLoader from 'theme/content-loader';
import Text from 'theme/text';
import BillingProfileCard from 'theme/billing-profile-card';
import PaymentAccountCard from 'theme/payment-account-card';
import BillingWithdrawalCard from 'theme/billing-withdrawal-card';
import Table from 'theme/table/billing';
import StatePassive from 'theme/icons/state-passive';
import StateActive from 'theme/icons/state-active';
import StateDraft from 'theme/icons/state-draft';
// import StatePending from 'theme/icons/state-pending';
import StatePaid from 'theme/icons/state-complete';
import translations from 'translations';
import selectProfile from 'redux-api/reselect/profile';

const Wrapper = styled.div`
  ${space}

  display: flex;
  align-items: center;
  flex-direction: column;

  & > div {
    width: 100%;
    max-width: 2100px;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 390px;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 16px 16px 16px 32px;
  margin: auto;

  ${space}
  ${border}
  ${color}
  ${layout}

  & > .body {
    width: 100%;
    padding-right: 16px;
  }
`;

const BillingOverview = styled.div`
  border-radius: 12px;
  background: linear-gradient(0deg, #e0f1f3 0%, #f5fafb 72.89%);
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  ${space}
`;

const Flex = styled.div`
  display: flex;

  ${flexbox}
  ${space}
`;

const Value = styled.div`
  ${color}

  display: flex;
  flex-direction: row;
  align-items: center;
`;

const RowPeriods = styled.div`
  display: flex;
  flex-direction: row;
`;

const PeriodLabel = styled.div`
  padding: 4px 8px;
  margin-right: 16px;
  background-color: ${({ disabled }) =>
    disabled ? themeGet('colors.grey_100') : themeGet('colors.primary_100')};
`;

function PanoramicBilling() {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();

  const [offset, setOffset] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderType, setOrderType] = useState('ref_date');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [invoiceType, setInvoiceType] = useState({
    status: null,
    type: null,
  });

  const { languageCode } = useSelector((state) => state.config);
  const { isWaitingDocuments, isProfilesWaiting } = useSelector((state) => state.billing);
  const billingSummary = useSelector(selectSummary);
  const billingDocuments = useSelector(selectDocuments);
  const billingProfile = useSelector(selectDefaultBillingProfile);
  const lastWithdrawal = useSelector(selectLastWithdrawal);
  const { isWithrawAvailable } = useSelector((state) => state.billing);
  const {
    payload: { company },
  } = useSelector(selectProfile);

  useEffect(() => {
    dispatch(getBillingSummaryRequest());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getBillingDocumentsRequest({
        per_page: pageSize,
        page: offset,
        order_by: `${orderType}:${orderDirection}`,
        status: invoiceType.status,
        typologyCode: invoiceType.type,
      }),
    );
  }, [dispatch, pageSize, offset, orderType, orderDirection, invoiceType]);

  useEffect(() => {
    dispatch(getBillingProfilesRequest());
    dispatch(isWithrawAvailableRequest());
    dispatch(getLastWithdrawalRequest());
  }, [dispatch]);

  const columns = useMemo(
    () => [
      {
        title: translations[languageCode].invoice,
        width: 130,
        dataIndex: 'typologyCode',
        key: 'typologyCode',
        fixed: 'left',
        render: (text, record) => (
          <Flex flexDirection="row" alignItems="center">
            {record.status === 'DRAFT' ? (
              <StateDraft />
            ) : text === 'PASSIVE' ? (
              <StatePassive />
            ) : (
              <StateActive />
            )}
            <Text
              ml="xs"
              fontSize="label"
              lineHeight="label"
              fontWeight="medium"
              color={
                record.status === 'DRAFT'
                  ? 'grey_05'
                  : text === 'PASSIVE'
                  ? 'primary_400'
                  : 'orange_400'
              }
            >
              {record.status === 'DRAFT'
                ? translations[languageCode].DRAFT
                : translations[languageCode][text]}
            </Text>
          </Flex>
        ),
      },
      {
        title: translations[languageCode].number,
        dataIndex: 'issuedNumber',
        key: 'issuedNumber',
        width: 150,
        render: (text) => <Text>{text || translations[languageCode]['no available']}</Text>,
      },
      {
        title: translations[languageCode].date,
        dataIndex: 'approvedOn',
        key: 'approvedOn',
        sorter: true,
        width: 150,
      },
      {
        title: translations[languageCode].paidOn,
        dataIndex: 'paidOn',
        key: 'paidOn',
        sorter: true,
        width: 150,
        render: (date) => (
          <Flex flexDirection="row" alignItems="center">
            {!date ? (
              <PeriodLabel disabled>
                <Text fontSize="pLarge" lineHeight="pLarge" fontWeight="medium" color="grey_400">
                  {translations[languageCode].periods_unavailable}
                </Text>
              </PeriodLabel>
            ) : (
              <>
                <Text
                  mr="xs"
                  fontSize="pLarge"
                  lineHeight="pLarge"
                  fontWeight="bold"
                  color="grey_600"
                >
                  {date}
                </Text>
                <StatePaid />
              </>
            )}
          </Flex>
        ),
      },
      {
        title: translations[languageCode].amount,
        dataIndex: 'amount',
        key: 'amount',
        sorter: true,
        width: 150,
        render: (text, record) =>
          record.invoiceType === 'DRAFT' ? (
            <Text fontSize="pLarge" lineHeight="pLarge" fontWeight="bold" color="grey_600">
              {text}
            </Text>
          ) : (
            <Value color={record.typologyCode === 'ACTIVE' ? 'red_400' : 'lime_400'}>
              {record.typologyCode === 'ACTIVE' ? '-' : '+'}
              <Text
                ml="xxs"
                fontSize="pLarge"
                lineHeight="pLarge"
                fontWeight="bold"
                color="grey_600"
              >
                {text}
              </Text>
            </Value>
          ),
      },
      {
        title: translations[languageCode]['Earning Periods'],
        width: 370,
        dataIndex: 'periods',
        key: 'periods',
        render: (periods) => (
          <RowPeriods>
            {periods.length === 0 ? (
              <PeriodLabel disabled>
                <Text fontSize="pLarge" lineHeight="pLarge" fontWeight="medium" color="grey_400">
                  {translations[languageCode].periods_unavailable}
                </Text>
              </PeriodLabel>
            ) : (
              <>
                {periods.slice(0, 3).map((period) => (
                  <PeriodLabel key={period}>
                    <Text
                      fontSize="pLarge"
                      lineHeight="pLarge"
                      fontWeight="medium"
                      color="primary_400"
                    >
                      {period}
                    </Text>
                  </PeriodLabel>
                ))}
                {periods.length > 3 && (
                  <PeriodLabel>
                    <Text
                      fontSize="pLarge"
                      lineHeight="pLarge"
                      fontWeight="medium"
                      color="primary_400"
                    >
                      {`+${periods.slice(3).length}`}
                    </Text>
                  </PeriodLabel>
                )}
              </>
            )}
          </RowPeriods>
        ),
      },
      /* {
        title: translations[languageCode].State,
        width: 130,
        dataIndex: 'status',
        key: 'status',
        fixed: 'right',
        render: (text) => (
          <Flex flexDirection="row" alignItems="center" justifyContent="flex-end">
            <Text
              mr="xs"
              fontSize="label"
              lineHeight="label"
              fontWeight={text === 'pending' ? 'medium' : 'bold'}
              color={text === 'pending' ? 'grey_400' : 'grey_600'}
            >
              {text}
            </Text>
            {text === 'paid' ? <StatePaid /> : <StatePending />}
          </Flex>
        ),
      }, */
    ],
    [languageCode],
  );

  const handleChange = useCallback(({ pagination: { current }, sorter }) => {
    const { order, columnKey } = sorter;

    if (current) {
      setOffset(current);
    }

    if (order) setOrderDirection(order === 'descend' ? 'desc' : 'asc');
    if (columnKey) setOrderType(columnKey);
  }, []);

  const handleChangeSize = useCallback((current, s) => {
    setPageSize(s);
  }, []);

  const invoiceTypeParsed = useMemo(() => {
    if (invoiceType.status === 'DRAFT') {
      return 'DRAFT';
    }

    if (invoiceType.type === 'PASSIVE') {
      return 'PASSIVE';
    }

    if (invoiceType.type === 'ACTIVE') {
      return 'ACTIVE';
    }

    return 'ALL';
  }, [invoiceType]);

  const handleChangeInvoicesType = useCallback((value) => {
    if (value === 'DRAFT') {
      return setInvoiceType({
        status: 'DRAFT',
        type: null,
      });
    }

    if (value === 'PASSIVE') {
      return setInvoiceType({
        status: null,
        type: 'PASSIVE',
      });
    }

    if (value === 'ACTIVE') {
      return setInvoiceType({
        status: null,
        type: 'ACTIVE',
      });
    }

    return setInvoiceType({
      status: null,
      type: null,
    });
  }, []);

  return (
    <Wrapper px="l" py="xs" my="xs">
      <div>
        <BillingOverview px="l" py="m" my="s">
          <Flex mr="xxl">
            <div>
              <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_400">
                {translations[languageCode]['Total earned']}
              </Text>
              <Text fontSize="h1Large" lineHeight="h1Large" fontWeight="bold" color="primary_600">
                {billingSummary.totalEarned === null ? (
                  <ContentLoader
                    width={350}
                    height={108}
                    lines={[
                      {
                        x: 0,
                        y: 10,
                        rx: 5,
                        ry: 5,
                        width: 350,
                        height: 98,
                      },
                    ]}
                  />
                ) : (
                  billingSummary.totalEarned
                )}
              </Text>
            </div>
          </Flex>

          <Flex flexDirection="row" justifyContent="space-between" pt="m">
            <Flex flexDirection="column" mr="xxl">
              <Text fontSize="h3" lineHeight="h3" fontWeight="medium" color="grey_400">
                {translations[languageCode]['Estimated next payment']}
              </Text>
              <Text fontSize="h2" lineHeight="h2" fontWeight="bold" color="grey_600">
                {billingSummary.estimatedPaymentOn === null ? (
                  <ContentLoader
                    width={125}
                    height={30}
                    lines={[
                      {
                        x: 0,
                        y: 5,
                        rx: 5,
                        ry: 5,
                        width: 125,
                        height: 25,
                      },
                    ]}
                  />
                ) : (
                  billingSummary.estimatedPaymentOn
                )}
              </Text>
              <Flex flexDirection="row" alignItems="center">
                <Text
                  fontSize="label"
                  lineHeight="label"
                  fontWeight="medium"
                  color="grey_400"
                  my="xxs"
                >
                  {translations[languageCode].Terms}:{' '}
                  <strong>{billingSummary.paymentTerm}</strong>
                </Text>
              </Flex>
            </Flex>

            <Flex flexDirection="column" mr="xxl">
              <Text fontSize="h3" lineHeight="h3" fontWeight="medium" color="grey_400">
                {translations[languageCode]['Next invoicing']}
              </Text>
              <Text fontSize="h2" lineHeight="h2" fontWeight="bold" color="grey_600">
                {billingSummary.nextInvoicing === null ? (
                  <ContentLoader
                    width={125}
                    height={30}
                    lines={[
                      {
                        x: 0,
                        y: 5,
                        rx: 5,
                        ry: 5,
                        width: 125,
                        height: 25,
                      },
                    ]}
                  />
                ) : (
                  billingSummary.nextInvoicing
                )}
              </Text>
              <Flex flexDirection="row" alignItems="center">
                <Text
                  fontSize="label"
                  lineHeight="label"
                  fontWeight="medium"
                  color="grey_400"
                  my="xxs"
                >
                  {translations[languageCode].Period}:{' '}
                  <strong>{billingSummary.invoicingPeriodCode}</strong>
                </Text>
              </Flex>
            </Flex>

            <Flex flexDirection="column" ml="xxl">
              <Text fontSize="h3" lineHeight="h3" fontWeight="medium" color="grey_400">
                {translations[languageCode]['Earnings not yet invoiced']}
              </Text>
              <Flex flexDirection="row" alignItems="center">
                <Text fontSize="h2" lineHeight="h2" fontWeight="bold" color="grey_600" mr="s">
                  {billingSummary.temporaryRevenuesNotBilled === null ? (
                    <ContentLoader
                      width={130}
                      height={30}
                      lines={[
                        {
                          x: 0,
                          y: 5,
                          rx: 5,
                          ry: 5,
                          width: 130,
                          height: 25,
                        },
                      ]}
                    />
                  ) : (
                    billingSummary.temporaryRevenuesNotBilled
                  )}
                </Text>
              </Flex>
            </Flex>

            <Flex flexDirection="column" ml="xxl">
              <Text fontSize="h3" lineHeight="h3" fontWeight="medium" color="grey_400">
                {translations[languageCode]['Last earnings']}
              </Text>
              <Flex flexDirection="row" alignItems="center">
                <Text fontSize="h2" lineHeight="h2" fontWeight="bold" color="grey_600" mr="s">
                  {billingSummary.lastEarned === null ? (
                    <ContentLoader
                      width={130}
                      height={30}
                      lines={[
                        {
                          x: 0,
                          y: 5,
                          rx: 5,
                          ry: 5,
                          width: 130,
                          height: 25,
                        },
                      ]}
                    />
                  ) : (
                    billingSummary.lastEarned
                  )}
                </Text>
                <Text fontSize="pLarge" lineHeight="pLarge" fontWeight="bold" color="grey_600">
                  {billingSummary.earningsPercentage === null ? (
                    <ContentLoader
                      width={50}
                      height={22}
                      lines={[
                        {
                          x: 0,
                          y: 5,
                          rx: 5,
                          ry: 5,
                          width: 45,
                          height: 20,
                        },
                      ]}
                    />
                  ) : (
                    <>
                      <span
                        style={{
                          color: themeGet(
                            billingSummary.earningsPercentage > 0
                              ? 'colors.lime_400'
                              : billingSummary.earningsPercentage < 0
                              ? 'colors.red_400'
                              : 'colors.grey_600',
                          )({ theme }),
                        }}
                      >
                        {billingSummary.earningsPercentage > 0
                          ? '+'
                          : billingSummary.earningsPercentage < 0
                          ? '-'
                          : ''}
                      </span>
                      {`${Math.abs(billingSummary.earningsPercentage)}%`}
                    </>
                  )}
                </Text>
              </Flex>

              <Text
                fontSize="label"
                lineHeight="label"
                fontWeight="medium"
                color="grey_400"
                my="xxs"
              >
                {translations[languageCode]['Compared to previous report']}
              </Text>
            </Flex>
          </Flex>
        </BillingOverview>

        {(!!isWithrawAvailable || lastWithdrawal.status !== 'processed') && (
          <Flex flexDirection="row">
            <div style={{ width: '100%', paddingRight: 8 }}>
              <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600" my="s">
                {translations[languageCode].WithdrawalAvailableTitle}
              </Text>
              <BillingWithdrawalCard
                style={{
                  mr: 'xs',
                  border: '1px solid',
                  borderColor: 'primary_200',
                }}
                lastWithdrawal={lastWithdrawal}
                languageCode={languageCode}
                isWithrawAvailable={isWithrawAvailable}
              />
            </div>
          </Flex>
        )}

        {!!billingProfile && (
          <Flex flexDirection="row">
            <div style={{ width: '50%', paddingRight: 8 }}>
              <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600" my="s">
                {translations[languageCode]['Billing Profile']}
              </Text>
              {isProfilesWaiting ? (
                <ContentLoader
                  width={400}
                  height={150}
                  lines={[
                    {
                      x: 0,
                      y: 0,
                      rx: 5,
                      ry: 5,
                      width: 400,
                      height: 150,
                    },
                  ]}
                />
              ) : (
                <BillingProfileCard
                  style={{
                    mr: 'xs',
                    border: '1px solid',
                    borderColor: 'primary_200',
                  }}
                  {...billingProfile}
                    subTitle={translations[languageCode][billingProfile.billingModeCode]}
                  languageCode={languageCode}
                />
              )}
            </div>
            <div style={{ width: '50%', paddingLeft: 8 }}>
              <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600" my="s">
                {translations[languageCode]['Payment Method']}
              </Text>
              {isProfilesWaiting ? (
                <ContentLoader
                  width={400}
                  height={150}
                  lines={[
                    {
                      x: 0,
                      y: 0,
                      rx: 5,
                      ry: 5,
                      width: 400,
                      height: 150,
                    },
                  ]}
                />
              ) : (
                <PaymentAccountCard
                  style={{
                    mr: 'xs',
                    border: '1px solid',
                    borderColor: 'primary_200',
                  }}
                  {...billingProfile.paymentAccount}
                  languageCode={languageCode}
                  paymentAccount={billingProfile.paymentAccount}
                  edit={false}
                />
              )}
            </div>
          </Flex>
        )}

        <Flex flexDirection="column">
          <Text fontSize="h3" lineHeight="h3" fontWeight="bold" color="grey_600" my="s">
            {translations[languageCode]['invoice list']}
          </Text>

          <Table
            emptyLabel={translations[languageCode]['no available']}
            columns={columns}
            dataSource={billingDocuments.data}
            pagination={{
              showSizeChanger: true,
              position: 'bottomCenter',
              pageSize,
              total: billingDocuments.totalCount,
              onShowSizeChange: handleChangeSize,
            }}
            total={billingDocuments.totalCount}
            onChangeTable={handleChange}
            invoicesType={invoiceTypeParsed}
            onChangeInvoicesType={handleChangeInvoicesType}
            loading={isWaitingDocuments}
            languageCode={languageCode}
            onRow={(record) => ({
              onClick: () => {
                if (record.typologyCode === 'ACTIVE' && record.platformDashCode === 'PUBLISH') {
                  return false;
                }
                return true;
              },
              style: {
                cursor: record.typologyCode === 'ACTIVE' && record.platformDashCode === 'PUBLISH' ? 'not-allowed' : 'pointer'
              }
            })}
          /> 
        </Flex>
      </div>
    </Wrapper>
  );
}

export default PanoramicBilling;
